/**
 * copyTokens — the ONE voice for the settings shell (P4, [D-NAME]/[D-NAME-2]
 * Carl-ruled). Every user-visible string in components/settings and
 * pages/settings comes from here or from StatusPill's state vocabulary.
 * If the entity word ever changes, this file is the whole change.
 */

import type { CapabilityState } from '@/hooks/useClientCapabilities';

/** [D-NAME] — the ruled entity word. */
const entity = 'workspace';
const brandBibleLabel = 'Brand bible';

export const COPY = {
  entity,

  brandBible: {
    deferralHeader: `Voice & words — managed in ${brandBibleLabel}`,
    deferralBadge: 'Read-only',
    deferralEmpty: `Not set in ${brandBibleLabel} yet`,
    concepts: { tone: 'Tone', avoidWords: 'Avoid words' },
    sections: { voiceMessaging: 'Voice & messaging', guardrails: 'Guardrails' },
    editLink: (section: string) => `Edit in ${brandBibleLabel} · ${section} →`,
  } as const,

  /** One primary recovery/creation action, used identically everywhere. */
  addEntity: `Add ${entity}`,
  /** The one empty-state template: what's missing + the one action. */
  chooseEntity: `Choose a ${entity}`,
  /** Unlinked deep-link state chip (header, amber variant). */
  unlinkedChip: `Not linked to a ${entity}`,

  /** Recovery-shaped save error (what happened + what to do — Krug Ch 11 goodwill). */
  saveFailed: `Settings didn't save — try again, or refresh the page.`,

  /** One-time transition notice (dismissible, localStorage-flagged; auto-expires — see ClientSettingsLayout.NOTICE_EXPIRY). */
  layoutNotice: 'New settings layout — your pages moved here.',
  layoutNoticeDismiss: 'Dismiss',

  /** First-run promoted entry point — visually distinct from amber. */
  startHere: 'Start here',

  /** Business landing: one-line welcome blurb above the form (≤ ~15 words). */
  businessBlurb: `The basics of this ${entity}: domain, location, and contact details.`,

  /** Accent chip accessible label (the swatch itself carries no text). */
  brandAccent: 'Brand accent',
  /** BrandBible preview chip — the post-correction dashboard accent on two grounds. */
  dashboardAccent: 'Dashboard accent',

  /** Plan chip labels. */
  plans: {
    seo: 'SEO',
    ads: 'Ads',
    both: 'SEO + Ads',
  },

  /** In-context tooltip per state term (title attributes on strip chips). */
  stateTooltips: {
    connected: 'Working as expected',
    action_needed: 'Needs your attention',
    not_set_up: 'Not configured yet',
  } satisfies Record<CapabilityState, string>,

  /**
   * Section labels — the single source for rail, page title, breadcrumbs and
   * chips. Page title = rail label verbatim (Krug page-name contract).
   */
  sections: {
    business: { label: 'Business info', path: '/settings' },
    integrations: { label: 'Integrations', path: '/settings/integrations' },
    content: { label: 'Content generation', path: '/settings/content' },
    website: { label: 'Website', path: '/settings/website' },
    media: { label: 'Media', path: '/settings/media' },
    team: { label: 'Team & account', path: '/settings/team' },

    /** Settings section field-map vocabulary (SET-ARCH-001). */
    businessInfo: {
      workspaceDomain: 'Workspace & domain',
      location: 'Location',
      googleDomainUrl: 'Google Domain URL',
      googleDomainUrlHelp: 'Used to scope SERP checks and geo-targeted research.',
      locality: 'City / Locality',
      region: 'Region / State',
      regionPlaceholder: 'Use 2-letter code if applicable',
      postalCode: 'Postal code',
      streetAddress: 'Street address',
      country: 'Country',
      contact: 'Contact details',
      contactHelp: 'Shown on landing pages and location schema.',
      disallowedLocations: 'Disallowed locations',
      disallowedLocationsHelp:
        'Comma-separated list of regions where we should not launch content.',
      savedAt: (value: string) => `Saved ${value}`,
      createdAt: (value: string) => `created ${value}`,
      notSavedYet: 'Not saved yet',
      /** Client status card (staff only). */
      clientStatus: {
        title: 'Client status',
        help:
          'Active clients get everything. Paused and offboarded clients get no writes from Core: no WordPress fixes, no plugin updates, no Google Ads budget changes. Planner budgets and the Google Ads MCC are separate and stay as they are.',
        options: { active: 'Active', paused: 'Paused', offboarded: 'Offboarded' } as const,
        confirmTitle: (site: string, status: string) => `Mark ${site} as ${status}?`,
        confirmBody: {
          active: 'Core resumes WordPress fixes, plugin updates and Google Ads pacing for this client on the next run.',
          paused: 'Core stops every write for this client until it is set back to active. The pacing report shows it as paused, not offboarded.',
          offboarded: 'Core stops every write for this client. Credentials, Ads ids and the Planner contract are not touched; clear or archive those separately if the client is gone for good.',
        } as const,
        confirmOk: 'Change status',
        cancel: 'Keep as is',
        saved: (status: string) => `Client status is now ${status}.`,
        failed: 'Status did not change. Try again, or tell engineering.',
      },
    },
    websiteInfo: {
      pageBuilder: 'Page builder',
      pageBuilderHelp:
        'Detect and configure the page builder used on this WordPress site for publishing.',
      siteIndexing: 'Site indexing',
      siteIndexingHelp:
        "Index your site's pages and generate AI summaries for internal linking and content planning.",
      locationPageTemplate: 'WordPress location-page template',
      locationPageTemplateHelp: 'Template slug used when deploying geo pages.',
    },
    mediaInfo: {
      brandImage: 'Brand image',
      primaryBrandImage: 'Primary brand image',
      primaryBrandImageHelp:
        'Displayed as the hero reference for generated landing pages.',
      noImageSelected: 'No image selected yet',
      imageUrlPlaceholder: 'Paste an image URL',
      imageUploadAction: 'Click or drag an image to add it to the gallery',
      imageUploadHelp: 'New uploads automatically replace the primary image.',
      additionalReferences: 'Additional references',
      primaryReferenceAlt: 'Primary reference',
      additionalReferenceAlt: 'Additional reference',
      imageUploadSucceeded: 'Image uploaded successfully!',
      findBrandIcon: 'Find brand icon',
      brandIconFound: 'Brand icon found and set as the primary image.',
      brandIconNotFound:
        'No brand icon could be confidently found — upload one instead.',
      brandIconFailed: 'Brand icon lookup failed. Please try again.',
    },
  },

  /** Nav aria-label for the settings rail (single source). */
  sectionsAriaLabel: 'Settings sections',

  /** Image-assets library vocabulary — Direction 3 Hybrid filters + grid. */
  mediaAssets: {
    noPreview: 'Source format — no web preview',
    humanApproved: 'Human-approved',
    aiLabeled: 'AI-labeled',
    openDetails: 'Open details',
    filterRegion: 'Asset filters',
    search: 'Search',
    searchPlaceholder: 'Filename, alt text, tags…',
    category: 'Category',
    allCategories: 'All categories',
    state: 'State',
    allStates: 'All states',
    labelStatus: 'Label status',
    allLabelStatuses: 'All label statuses',
    moreFilters: 'More filters',
    showArchived: 'Show archived',
    showArchivedHelp: 'Include archived assets.',
    activeFilters: 'Active filters',
    removeFilter: (label: string) => `Remove ${label} filter`,
    resultCount: (count: number) => `${count.toLocaleString()} asset${count === 1 ? '' : 's'}`,
    showingRange: (start: number, end: number, total: number) =>
      `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`,
    previous: 'Previous',
    next: 'Next',
    loading: 'Loading assets…',
    noWorkspaceTitle: 'Pick a workspace',
    noWorkspaceDescription: 'Select a workspace to browse its image assets.',
    emptyTitle: 'No assets yet',
    emptyDescription: 'No image assets match the current filters for this workspace.',
    errorTitle: 'Could not load assets',
    errorDescription: 'Something went wrong fetching this workspace’s assets. Try adjusting the filters or reloading.',
    categories: {
      product: 'Product',
      project: 'Project',
      location: 'Location',
      team: 'Team',
      gallery: 'Gallery',
      logo: 'Logo',
      service: 'Service',
      other: 'Other',
    },
    states: {
      webReady: 'Web-ready',
      sourceOnly: 'Source file',
      needsReview: 'Needs review',
      doNotUse: 'Do not use',
      archived: 'Archived',
    },
    labelStatuses: {
      labeled: 'Labeled',
      needsReview: 'Needs review',
      approved: 'Approved',
      doNotUse: 'Do not use',
    },
  },

  /** The Workspace-ID field (TenantSettingsPage basic tab) — one vocabulary. */
  workspaceId: {
    label: 'Workspace ID',
    tooltip: `Changing this value updates the ${entity} identifier everywhere it is referenced.`,
    extra: `Switch ${entity} from the global header to edit a different record.`,
    required: `Please enter a ${entity} ID`,
  },

  /** The agency-level Admin Panel (Settings → Platform Admin; O47). */
  adminPanel: {
    label: 'Admin Panel',
    path: '/admin',
    /** Where the Google Ads connect/manage links land (the old default tab). */
    integrationsPath: '/admin?tab=integrations',
  },
  /** Honest empty state for non-elevated visits to the Admin Panel. */
  platformEmpty: `The Admin Panel is for agency administrators. Choose a ${entity} to manage its settings.`,

  /** IntegrationCard (P3) — one vocabulary across all three cards. */
  cards: {
    /** Single-form dirty navigation prompt (owned by the guard slice). */
    dirtyGuard: {
      prompt: 'You have unsaved changes — leave anyway?',
      discard: 'Discard',
      stay: 'Stay',
      save: 'Save',
    },
    /** Product object names — the single source for strip chips + card titles. */
    objectNames: {
      brandBible: 'Brand bible',
      wordpress: 'WordPress',
      googleAds: 'Google Ads',
      googleDocs: 'Google Docs',
    } as const,
    detailsShow: 'Show details',
    detailsHide: 'Hide details',
    /** The one client-account action. */
    mapAccount: 'Map account',
    /** Bottleneck attribution for the factual line. */
    mappingRequired: 'Account mapping required',
    agencyConnectionRequired: 'Agency connection required',
    /** Broken-upstream reasons rendered WITH the disabled downstream action. */
    requiresPlan: 'Requires an active plan',
    requiresAgencyConnection: 'Agency connection down — managed in the Admin Panel',
    requiresAgencyReconnect: 'Agency connection revoked — managed in the Admin Panel',
    /** Connection-layer agency OAuth status lines — the read-only cascade row
     * (GA-S5 PP-2 single-sourcing; values verbatim, no copy change). */
    agencyConnectionActive:
      'Agency connection active — covers all workspaces under the manager account.',
    noAgencyConnection: 'No agency connection — managed in the Admin Panel',
    /** Scope lines — one recipe, per-card scope. */
    scopeGoogleAds: 'Agency-level connection · managed in the Admin Panel',
    scopeWordPress: `Connection is for this ${entity} only`,
    scopeGoogleDocs: `Exports go to this ${entity}'s Drive folder`,
    /** Plan-excluded honest empty state (deep link). */
    adsPlanEmpty: `Google Ads isn't part of this ${entity}'s plan.`,
    /** Cascade layer labels — "Management tier", never bare "Tier". */
    cascadePlan: 'Plan',
    cascadeConnection: 'Connection',
    cascadeMapping: 'Mapping',
    managementTier: 'Management tier',
    /** The cascade ops row — link-out only, config never duplicated here. */
    cascadeOps: 'Ops',
    weeklyManagement: 'Weekly management',
    /** Admin-gated pointer on the agency row (absence, never disabled) —
     * it names its destination. */
    manage: 'Manage agency connection',
    /** Agency-row provenance (GA1-B contract, GA-S2 same-event pair).
     * connectedBy / connectedOn are the WORDING AUTHORITY — the single source
     * for the sentence shape. The component composes the emphasized line from
     * the connector fragments below (who + date pop at medium weight, the
     * connectors stay muted); a sync-pin test asserts the composed textContent
     * EQUALS these flat forms so the fragments can never silently diverge
     * (S8 single-source lesson, enforced by test). */
    connectedBy: (who: string, date: string) => `Connected by ${who} on ${date}`,
    connectedOn: (date: string) => `Connected on ${date}`,
    /** Connector fragments for the emphasized provenance composition (GA-S2,
     * ruling 1 / Option D). Structural copy parts — kept in sync with the flat
     * authority above by the sync-pin test. */
    connectedByLead: 'Connected by',
    connectedDateLead: 'Connected on',
    connectedOnPrep: 'on',
    /** Candid stub for a CONNECTED agency with no recorded grant detail
     * (GA-S2, [UXF-GA2-PROV-KRUG] adjudication): specific, no false claim, no
     * date/email tokens. Absent everywhere else. */
    connectionDetailsNotRecorded: 'Connection details not recorded',
    /** R3 — the collapsed disclosure label for the agent-config group. */
    campaignDefaults: 'Campaign defaults',
    /** R5 — Postel guard message on manual mapping save. */
    customerIdInvalid: 'Customer ID must be 10 digits',
    /** R3 — the Ads factual line differentiates pending/revoked (pill stays 3-state). */
    adsPending: 'Authorization in progress',
    adsRevoked: 'Access revoked — reconnect',
    /** Disconnect lock-in — names the blast radius, always. */
    disconnect: 'Disconnect',
    cancel: 'Cancel',
    disconnectWordPressTitle: 'Disconnect WordPress?',
    disconnectWordPressBlast: (domain: string) =>
      `Publishing to ${domain} will fail until you reconnect. Existing drafts and posts stay untouched.`,
    disconnectGoogleAdsTitle: 'Disconnect Google Ads?',
    disconnectGoogleAdsBlast: `This disconnects Google Ads for ALL ${entity}s. Reconnect it in the Admin Panel to restore access.`,
  },
} as const;

export type SettingsSectionId = keyof typeof COPY.sections;

/** Rail order — the rail renders this array, nothing else. */
export const SETTINGS_SECTIONS: ReadonlyArray<{
  id: SettingsSectionId;
  label: string;
  path: string;
}> = [
  { id: 'business', ...COPY.sections.business },
  { id: 'integrations', ...COPY.sections.integrations },
  { id: 'content', ...COPY.sections.content },
  { id: 'website', ...COPY.sections.website },
  { id: 'media', ...COPY.sections.media },
  { id: 'team', ...COPY.sections.team },
];

/** State vocabulary is owned by StatusPill; re-exported here as one source. */
export {
  STATUS_PILL_STATE_LABEL as STATE_LABELS,
  NOT_VERIFIED_SUFFIX,
  staleSuffixFor,
} from '@/components/settings/StatusPill';
